/**
 * Admin → White-label branding (logo / primary color / custom domain
 * / transactional email identity that the reseller's customers see).
 *
 * Backed by /v1/admin/branding; same RequireReseller gate as the rest
 * of /v1/admin/* — customer-tenant users get 403.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface Branding {
  logo_url?: string;
  primary_color?: string;
  custom_domain?: string;
  domain_verified: boolean;
  email_from_name?: string;
  email_reply_to?: string;
}

export interface BrandingInput {
  logo_url: string;
  primary_color: string;
  custom_domain: string;
  email_from_name: string;
  email_reply_to: string;
}

const BRANDING_KEY = ['admin', 'branding'] as const;

export function useBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<Branding>('/v1/admin/branding'),
    staleTime: 60_000,
  });
}

export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandingInput) => api.put<Branding>('/v1/admin/branding', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BRANDING_KEY });
      // Also bust the runtime branding cache so AppShell's CSS-var
      // override + the sidebar logo re-paint immediately when a
      // reseller saves changes to their own branding (no need for a
      // hard reload).
      void qc.invalidateQueries({ queryKey: ['branding', 'current'] });
    },
  });
}
